import {
  FC,
  HTMLAttributes,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  NotificationService,
  NotificationServiceProps,
  nudge,
  toast
} from '../../../service/NotificationService';

import { Devices } from '../../../service/Devices';
import { NotificationProps } from '../../jdsnudge/logic';
import { NudgeContainer } from '../../jdsnudgecontainer/generated';
import { ToastContainer } from '../../jdstoastcontainer/generated';
import { subscribe } from '../../../service/pubsub';

export interface NotificationContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  /**
   * Triggered when nudge or toast are showed on screen
   */
  onShow?: (id: string) => void;
  /**
   * Triggered when nudge or toast are closed
   */
  onClose?: (id: string) => void;
}

/**
 * The `<NotificationContainer />` component extends `HTMLAttributes<HTMLDivElement>`.
 * @param props
 * @constructor
 */
const NotificationContainer: FC<NotificationContainerProps> = (
  props: NotificationContainerProps
): ReactElement => {
  const { className, onShow, onClose, ...rest } = props;
  const classes = [className, 'j-notificationContainer'].join(' ');
  const notificationContRef = useRef(null);
  const [nudges, setNudges] = useState<NotificationProps[]>([]);
  const [toasts, setToasts] = useState<NotificationProps[]>([]);

  const maxOnScreen = Devices.isMobile() ? 1 : 5;

  useEffect(() => {
    const { unsubscribe: nudgeUnsubscribe } = subscribe(
      NotificationService,
      (data: NotificationServiceProps) => {
        if (data.type === 'nudge') setNudges(data.notifications);
      }
    );
    const { unsubscribe: toastUnsubscribe } = subscribe(
      NotificationService,
      (data: NotificationServiceProps) => {
        if (data.type === 'toast') setToasts(data.notifications);
      }
    );
    return () => {
      nudge.dismissAll();
      nudgeUnsubscribe();
      toast.dismissAll();
      toastUnsubscribe();
    };
  }, []);

  return (
    <div className={classes} {...rest} ref={notificationContRef}>
      <NudgeContainer
        onShow={onShow}
        onClose={onClose}
        nudges={nudges.slice(0, maxOnScreen).reverse()}
      />
      <ToastContainer
        onShow={onShow}
        onClose={onClose}
        toasts={toasts.slice(0, maxOnScreen).reverse()}
      />
    </div>
  );
};

NotificationContainer.displayName = 'NotificationContainer';
NotificationContainer.defaultProps = {};
export { NotificationContainer };
